import { useEffect, useMemo, useRef, useState } from 'react'
import { RefreshCw, PhoneIncoming, PhoneOutgoing, MoonStar } from 'lucide-react'
import { apiGet } from '../lib/api.js'
import { ApptTypeFilter } from '../components/ApptType.jsx'
import {
  Segmented, DateRangePicker, Field, IconButton, Loading, ErrorState, EmptyState,
  fmtDate, fmtMonth, fmtNumber, bkkTodayISO, TH_MONTHS_SHORT,
} from '../kit'

// integer counts with thousand separators (percents / durations format elsewhere)
const fmtN = (n) => fmtNumber(Math.round(Number(n) || 0))
// a YYYY-MM-DD span on screen: dd/mm/yyyy (kit format)
const fmtSpan = (a, b) => (a === b ? fmtDate(a) : `${fmtDate(a)} – ${fmtDate(b)}`)

/**
 * Call quality dashboard (สายโทรศัพท์ → ภาพรวม).
 *
 *  INBOUND   answered vs missed — split into IN-office-hours (the agent KPI) and
 *            OFF-hours (last_destination = smileseasonsClose, excluded from the KPI
 *            but tracked separately, since a rising off-hour miss rate = night-shift signal).
 *    answered   → ring_time = time to answer, call_duration = talk time
 *    missed     → call_duration = wait before the caller hung up (ring_time is 0)
 *                 done_timestamp − created_at = time until the callback was cleared
 *  OUTBOUND  answered vs cancelled
 *    answered  → call_duration = talk time
 *    cancelled → ring_time = how long the agent waited before hanging up
 *
 * % is the headline everywhere (it's the KPI). Filters: date range · patient type.
 * Every number trends vs. the immediately-preceding equal-length period.
 */

const BKK = 7 * 60 * 60 * 1000
const instantOf = (c) => (c.start_stamp ? Number(c.start_stamp) - BKK : Number(c.created_at || 0))
// external party number + match key (last 9 digits) — mirrors CallList, used to line
// up an outbound callback with the inbound missed call it answers.
const partyOf = (c) => (c.direction === 'outbound' ? (c.caller_destination || c.last_destination) : (c.caller_id_number || c.source_number)) || c.caller_id_number || c.caller_destination || ''
const normNum = (n) => { const d = String(n || '').replace(/\D/g, ''); return d.length > 9 ? d.slice(-9) : d }
const bkk = { timeZone: 'Asia/Bangkok' }
const todayBkk = () => bkkTodayISO()
function shiftDay(day, delta) {
  const [y, m, d] = day.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + delta)
  return dt.toISOString().slice(0, 10)
}
function daysBetween(start, end) { const out = []; let d = start; for (let i = 0; i < 400 && d <= end; i++) { out.push(d); d = shiftDay(d, 1) } return out }
// Continuous list of YYYY-MM month keys spanning start..end (inclusive).
function monthsBetween(start, end) {
  const out = []; let [y, m] = start.split('-').map(Number); const [ey, em] = end.split('-').map(Number)
  for (let i = 0; i < 120; i++) {
    out.push(`${y}-${String(m).padStart(2, '0')}`)
    if (y > ey || (y === ey && m >= em)) break
    m++; if (m > 12) { m = 1; y++ }
  }
  return out
}
const bkkHour = (ms) => parseInt(new Date(ms).toLocaleString('en-GB', { ...bkk, hour12: false, hour: '2-digit' }), 10)
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const bkkDow = (ms) => DOW.indexOf(new Date(ms).toLocaleString('en-US', { ...bkk, weekday: 'short' }))

const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0)
const meanOf = (arr, f) => { const v = arr.map(f).map(Number).filter((x) => !isNaN(x)); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : 0 }
const median = (arr) => { if (!arr.length) return 0; const s = [...arr].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2 }
const fmtDur = (sec) => { const s = Math.max(0, Math.round(sec)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }
function fmtElapsed(ms) {
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s} วิ`
  const min = Math.round(s / 60)
  if (min < 60) return `${min} นาที`
  return `${(min / 60).toFixed(1)} ชม.`
}

const OFF_HOUR = 'นอกเวลาทำการ'
function missReason(dest) {
  const d = String(dest || '')
  if (d === 'SMILEGreetingIVR') return 'วางสายก่อนเข้าเมนู'
  if (d === 'smileseasonsNumberCallBack') return 'ฝากเบอร์ให้โทรกลับ'
  if (/Press2New_IVR/i.test(d)) return 'กด 2 (คนไข้เก่า) ไม่มีคนรับ'
  if (/^smileseasonsFull/i.test(d)) return 'สายเต็ม ไม่รอ'
  if (d === 'smileseasonsClose') return OFF_HOUR
  if (/^\d{2,4}$/.test(d)) return 'ถึงเอเจนต์ แต่ไม่รับสาย'
  return 'อื่นๆ'
}
// office-hour reasons only (off-hour is split out and shown separately)
const REASON_ORDER = ['ถึงเอเจนต์ แต่ไม่รับสาย', 'กด 2 (คนไข้เก่า) ไม่มีคนรับ', 'ฝากเบอร์ให้โทรกลับ', 'สายเต็ม ไม่รอ', 'วางสายก่อนเข้าเมนู', 'อื่นๆ']

// One ranged request stream (type=default → full records + addons), page-walked
// until the last page. Replaces the old day-by-day fan-out now that the endpoint
// takes start_date/end_date. Cap = 120 pages (60k rows) — far above any real range.
async function fetchRange(start, end, apptType) {
  const out = []; let page = 1
  for (let i = 0; i < 120; i++) {
    const r = await apiGet('/api/phone', { start_date: start, end_date: end, type: 'default', page, per_page: 500, appointmentlist_id: apptType || undefined })
    const items = Array.isArray(r) ? r : Array.isArray(r?.items) ? r.items : []
    out.push(...items)
    const cur = Number(r?.curPage ?? page); const total = Number(r?.pageTotal ?? 1)
    if (!items.length || cur >= total) break
    page = cur + 1
  }
  return out
}

// Long-range %missed trend via the server-side monthly aggregate (type=stat) —
// ONE lightweight request instead of paging a year of full records. Returns
// { 'YYYY-MM': {ans, miss} } (inbound answered / missed per month).
//
// Handles BOTH stat eval shapes so it works whichever one is published:
//  (A) grouped rows  { year, month, direction:"inbound", call_result:"missed", n }
//      → sum n per inbound answered/missed (includes off-hour missed)
//  (B) flag rows     { year, month, in_ans, in_miss }
//      → office-hour split done server-side (off-hour excluded)
async function fetchStat(start, end, force) {
  const r = await apiGet('/api/phone', { type: 'stat', start_date: start, end_date: end, ...(force ? { nocache: 1 } : {}) })
  const rows = Array.isArray(r) ? r : Array.isArray(r?.items) ? r.items : Array.isArray(r?.result) ? r.result : []
  const by = {}
  for (const g of rows) {
    const y = Number(g.year), mo = Number(g.month)
    if (!y || !mo) continue
    const key = `${y}-${String(mo).padStart(2, '0')}`
    const b = by[key] || (by[key] = { ans: 0, miss: 0 })
    if (g.in_ans != null || g.in_miss != null) {           // shape B (office-hour split)
      b.ans += Number(g.in_ans || 0); b.miss += Number(g.in_miss || 0)
    } else if (typeof g.direction === 'string') {           // shape A (grouped by dir + result)
      const n = Number(g.n ?? g.count ?? g.total ?? 0)
      if (g.direction === 'inbound') {
        if (g.call_result === 'answered') b.ans += n
        else if (g.call_result === 'missed') b.miss += n
      }
    }
  }
  return by
}

function heat(calls, valueFn) {
  const count = Array.from({ length: 7 }, () => Array(24).fill(0))
  const sum = Array.from({ length: 7 }, () => Array(24).fill(0))
  calls.forEach((c) => {
    const ms = instantOf(c); const d = bkkDow(ms); const h = bkkHour(ms)
    if (d >= 0 && d < 7 && h >= 0 && h < 24) { count[d][h]++; if (valueFn) sum[d][h] += Number(valueFn(c)) || 0 }
  })
  return { count, sum }
}
const meanMatrix = (h) => h.count.map((row, d) => row.map((n, hr) => (n ? h.sum[d][hr] / n : 0)))

function compute(rows, nDays) {
  const inbound = rows.filter((c) => c.direction === 'inbound')
  const outbound = rows.filter((c) => c.direction === 'outbound')
  const inAns = inbound.filter((c) => c.call_result === 'answered')
  const allMiss = inbound.filter((c) => c.call_result === 'missed')
  const offHour = allMiss.filter((c) => missReason(c.last_destination) === OFF_HOUR)
  const officeMiss = allMiss.filter((c) => missReason(c.last_destination) !== OFF_HOUR)

  // KPI scope = office hours only (calls agents could actually have answered)
  const officeInbound = inAns.length + officeMiss.length
  const missPct = officeInbound ? (officeMiss.length / officeInbound) * 100 : 0
  const ansPct = officeInbound ? (inAns.length / officeInbound) * 100 : 0

  const missDone = officeMiss.filter((c) => c.done && c.done_timestamp && c.created_at)
  const clearMs = missDone.map((c) => Number(c.done_timestamp) - Number(c.created_at)).filter((x) => x > 0)
  const clearMedian = median(clearMs)
  const within1h = clearMs.length ? (clearMs.filter((x) => x <= 3600000).length / clearMs.length) * 100 : 0

  const reasons = REASON_ORDER.map((label) => ({ label, n: officeMiss.filter((c) => missReason(c.last_destination) === label).length })).filter((r) => r.n > 0)

  const outAns = outbound.filter((c) => c.call_result === 'answered')
  const outCanc = outbound.filter((c) => c.call_result === 'cancelled')

  // Close quality: of the missed calls marked เสร็จสิ้น, how many have NO callback
  // trace (no outbound dial-back and no inbound answered-back to that number, after
  // the miss)? A bare close = ticket cleared without the phone log showing any
  // contact. Uses ALL missed (office + off-hour) since the worklist works them all.
  const ev = new Map()
  for (const c of rows) {
    if (c.direction === 'outbound' || (c.direction === 'inbound' && c.call_result === 'answered')) {
      const k = normNum(partyOf(c)); if (!k) continue
      const ts = instantOf(c); if (ts > (ev.get(k) || 0)) ev.set(k, ts)
    }
  }
  const hasCallback = (c) => { const cb = ev.get(normNum(partyOf(c))) || 0; return cb > instantOf(c) }
  const doneMiss = allMiss.filter((c) => c.done)
  const bareClose = doneMiss.filter((c) => !hasCallback(c))
  const closedNoCbPct = doneMiss.length ? (bareClose.length / doneMiss.length) * 100 : 0
  const empMap = new Map()
  for (const c of doneMiss) {
    const code = c.employee_done_by?.code || (c.done_by ? `#${c.done_by}` : '—')
    const e = empMap.get(code) || (empMap.set(code, { code, total: 0, noCb: 0 }), empMap.get(code))
    e.total++; if (!hasCallback(c)) e.noCb++
  }
  const closeByEmp = [...empMap.values()].map((e) => ({ ...e, pct: e.total ? (e.noCb / e.total) * 100 : 0 }))
    .sort((a, b) => b.noCb - a.noCb || b.total - a.total)

  return {
    closedN: doneMiss.length, closedNoCbN: bareClose.length, closedNoCbPct, closeByEmp,
    inTotal: inbound.length, inPerDay: nDays ? inbound.length / nDays : 0,
    officeInbound, offHourN: offHour.length, officeMissedN: officeMiss.length,
    missPct, ansPct, inAnsN: inAns.length,
    inAnsRing: meanOf(inAns, (c) => c.ring_time), inAnsTalk: meanOf(inAns, (c) => c.call_duration),
    missWait: meanOf(officeMiss, (c) => c.call_duration),
    clearMedian, clearN: missDone.length, within1h,
    reasons,
    outTotal: outbound.length, outPerDay: nDays ? outbound.length / nDays : 0,
    outAnsN: outAns.length, outCancN: outCanc.length, outAnsPct: outbound.length ? (outAns.length / outbound.length) * 100 : 0, outCancPct: outbound.length ? (outCanc.length / outbound.length) * 100 : 0,
    outAnsTalk: meanOf(outAns, (c) => c.call_duration), outCancRing: meanOf(outCanc, (c) => c.ring_time),
    hmInVol: heat(inbound).count, hmMissVol: heat(officeMiss).count, hmMissWait: meanMatrix(heat(officeMiss, (c) => c.call_duration)), hmOffHour: heat(offHour).count, hmOutVol: heat(outbound).count,
  }
}

const opts = (pairs) => pairs.map(([value, label]) => ({ value, label }))
const PRESETS = opts([['today', 'วันนี้'], ['7d', '7 วัน'], ['14d', '14 วัน'], ['30d', '30 วัน'], ['custom', 'กำหนดเอง']])
const PATIENTS = opts([['all', 'ทั้งหมด'], ['existing', 'คนไข้เก่า'], ['new', 'คนไข้ใหม่']])
const HEAT_OPTS = opts([['vol', 'สายเข้า'], ['miss', 'ไม่ได้รับ'], ['wait', 'เวลารอ'], ['off', 'นอกเวลา']])

export default function CallDashboard({ reloadKey = 0 }) {
  const [preset, setPreset] = useState('7d')
  const [cStart, setCStart] = useState(() => shiftDay(todayBkk(), -6))
  const [cEnd, setCEnd] = useState(() => todayBkk())
  const [patient, setPatient] = useState('all')
  const [apptType, setApptType] = useState('')      // appointmentList id ('' = ทุกประเภท)
  // The last loaded set, together with the range it was loaded FOR — a refresh or a
  // range change keeps it on screen (dimmed) until the new one lands, and the
  // numbers stay computed against their own range (no half-new mix).
  const [data, setData] = useState(null)       // { rows, range }
  // Bumped on every finished load. The monthly trend panel is keyed on it: before the
  // kit move the panel unmounted with each reload and fetched its stat again on
  // remount; keeping that key keeps the API calls exactly as they were.
  const [loadSeq, setLoadSeq] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tick, setTick] = useState(0)
  const [inHeat, setInHeat] = useState('vol')

  // header refresh (in CallList) bumps reloadKey → refetch, replacing the dashboard's
  // own top refresh button (which lived lower down in the toolbar).
  useEffect(() => { if (reloadKey) setTick((t) => t + 1) }, [reloadKey])

  const range = useMemo(() => {
    let start, end
    if (preset === 'custom') { start = cStart; end = cEnd } else { end = todayBkk(); start = shiftDay(end, -(preset === '30d' ? 29 : preset === '14d' ? 13 : preset === 'today' ? 0 : 6)) }
    if (start > end) [start, end] = [end, start]
    const nDays = daysBetween(start, end).length
    const prevEnd = shiftDay(start, -1); const prevStart = shiftDay(prevEnd, -(nDays - 1))
    return { start, end, nDays, prevStart, prevEnd, hasTrend: nDays <= 31 }
  }, [preset, cStart, cEnd])

  useEffect(() => {
    let alive = true; setLoading(true); setError(null)
    const from = range.hasTrend ? range.prevStart : range.start
    fetchRange(from, range.end, apptType)
      .then((rows) => { if (alive) { setData({ rows, range }); setLoadSeq((n) => n + 1); setLoading(false) } })
      .catch((e) => { if (alive) { setError(e.message || 'โหลดไม่สำเร็จ'); setLoading(false) } })
    return () => { alive = false }
  }, [range, apptType, tick])

  const { cur, prev } = useMemo(() => {
    if (!data) return { cur: null, prev: null }
    const { rows, range } = data
    const pf = (arr) => (patient === 'all' ? arr : patient === 'new' ? arr.filter((c) => !c.patient_id) : arr.filter((c) => Number(c.patient_id) > 0))
    const inR = (c, s, e) => c.date >= s && c.date <= e
    const curRows = pf(rows.filter((c) => inR(c, range.start, range.end)))
    const prevRows = pf(rows.filter((c) => inR(c, range.prevStart, range.prevEnd)))
    return { cur: compute(curRows, range.nDays), prev: range.hasTrend ? compute(prevRows, range.nDays) : null }
  }, [data, patient])

  const m = cur

  return (
    <div>
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <Segmented value={preset} onChange={setPreset} options={PRESETS} scroll />
        {preset === 'custom' && (
          <DateRangePicker className="w-[260px]" value={{ start: cStart, end: cEnd }}
            onChange={({ start, end }) => { if (start && end) { setCStart(start); setCEnd(end) } }} />
        )}
        <span className="text-[12.5px] text-slate-400 tabular-nums">{fmtSpan(range.start, range.end)} · {range.nDays} วัน</span>
        {loading && m && <Loading variant="inline" label="กำลังคำนวณ…" />}
      </div>
      <div className="mb-5 flex items-end gap-3 flex-wrap">
        <Field label="ประเภทคนไข้"><Segmented value={patient} onChange={setPatient} options={PATIENTS} /></Field>
        <Field label="ประเภทนัด"><ApptTypeFilter value={apptType} onChange={setApptType} width="w-[190px]" /></Field>
        {range.hasTrend
          ? <span className="pb-2 text-[11.5px] text-slate-400 tabular-nums">เทียบช่วงก่อนหน้า ({fmtSpan(range.prevStart, range.prevEnd)})</span>
          : <span className="pb-2 text-[11.5px] text-slate-400">ช่วงยาวเกิน 31 วัน — ไม่แสดงแนวโน้ม</span>}
      </div>

      {error ? (
        <ErrorState message={error} onRetry={() => setTick((t) => t + 1)} />
      ) : !m ? (
        <Loading label="กำลังคำนวณ…" />
      ) : (
        <div className={`space-y-6 transition-opacity ${loading ? 'opacity-60' : ''}`}>
          {/* INBOUND */}
          <div>
            <SectionHead icon={PhoneIncoming} tone="text-emerald-600" title="สายเข้า (Inbound)" />
            <MindMap
              root={<RootNode label="สายเข้าทั้งหมด" value={fmtN(m.inTotal)} sub={`ในเวลา ${fmtN(m.officeInbound)} · นอกเวลา ${fmtN(m.offHourN)}`} cur={m.inTotal} prev={prev?.inTotal} />}
              branches={[
                <BranchNode key="ans" label="รับสาย" value={`${Math.round(m.ansPct)}%`} badge={`${fmtN(m.inAnsN)} สาย`}
                  tone="text-emerald-600" accent="border-emerald-200" cur={m.ansPct} prev={prev?.ansPct} betterWhen="up"
                  metrics={[
                    { label: 'รอก่อนรับเฉลี่ย', value: fmtDur(m.inAnsRing), cur: m.inAnsRing, prev: prev?.inAnsRing, betterWhen: 'down' },
                    { label: 'สนทนาเฉลี่ย', value: fmtDur(m.inAnsTalk), cur: m.inAnsTalk, prev: prev?.inAnsTalk },
                  ]} />,
                // ไม่ได้รับสาย is a category that splits into IN-hours (the agent KPI) and
                // OFF-hours (night, no agent on shift). Both sit side-by-side to the right of
                // the parent, since off-hour is a KIND of missed call — not its own top branch.
                <div key="miss" className="flex items-center">
                  <BranchNode compact label="ไม่ได้รับสาย" value={fmtN(m.officeMissedN + m.offHourN)} badge={`${pct(m.officeMissedN + m.offHourN, m.inTotal)}% ของสายเข้า`}
                    tone="text-rose-600" accent="border-rose-200"
                    cur={m.officeMissedN + m.offHourN} prev={prev ? prev.officeMissedN + prev.offHourN : null} betterWhen="down" />
                  <div className="w-7 border-t-2 border-slate-200 flex-none" />
                  <div className="mindtree flex flex-col gap-3 flex-none py-1">
                    <div className="mind-branch">
                      <BranchNode label="ในเวลาทำการ · KPI" value={`${m.missPct.toFixed(1)}%`} badge={`${fmtN(m.officeMissedN)} สาย`}
                        tone="text-rose-600" accent="border-rose-300" emphasis cur={m.missPct} prev={prev?.missPct} betterWhen="down"
                        metrics={[
                          { label: 'รอก่อนวางสาย', value: m.officeMissedN ? fmtDur(m.missWait) : '—', cur: m.missWait, prev: prev?.missWait },
                          { label: 'ตามกลับ (มัธยฐาน)', value: m.clearN ? fmtElapsed(m.clearMedian) : '—', cur: m.clearMedian, prev: prev?.clearMedian, betterWhen: 'down' },
                          { label: 'ตามกลับใน 1 ชม.', value: m.clearN ? `${Math.round(m.within1h)}%` : '—', cur: m.within1h, prev: prev?.within1h, betterWhen: 'up' },
                        ]} />
                    </div>
                    <div className="mind-branch">
                      <BranchNode icon={MoonStar} label="นอกเวลาทำการ" value={fmtN(m.offHourN)} badge={`${pct(m.offHourN, m.inTotal)}% ของสายเข้า`}
                        tone="text-indigo-600" accent="border-indigo-200" cur={m.offHourN} prev={prev?.offHourN} betterWhen="down"
                        foot="ไม่นับใน KPI เอเจนต์ — ถ้าเพิ่มขึ้น อาจต้องมีกะกลางคืน" />
                    </div>
                  </div>
                </div>,
              ]} />

            <MissedTrendPanel key={loadSeq} />

            <Panel title="สาเหตุที่ไม่ได้รับสาย · ในเวลาทำการ (last_destination)">
              {m.reasons.length === 0 ? (
                <EmptyState variant="compact" title="ไม่มีสายที่ไม่ได้รับในช่วงนี้" />
              ) : (
                <div className="space-y-2">
                  {m.reasons.map((r) => (
                    <div key={r.label} className="flex items-center gap-3 text-[12.5px]">
                      <span className="w-44 flex-none text-slate-600">{r.label}</span>
                      <div className="flex-1 h-4 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-rose-400" style={{ width: `${pct(r.n, m.officeMissedN)}%` }} /></div>
                      <span className="w-20 flex-none text-right tabular-nums text-slate-500">{fmtN(r.n)} · {pct(r.n, m.officeMissedN)}%</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="คุณภาพการปิดเคส · ปิดโดยไม่มีสายโทรกลับ">
              <CloseQuality m={m} prev={prev} />
            </Panel>

            <Panel title="แผนที่ความหนาแน่น · วัน × ชั่วโมง"
              right={<Segmented size="sm" value={inHeat} onChange={setInHeat} options={HEAT_OPTS} scroll />}>
              {inHeat === 'vol' && <Heatmap matrix={m.hmInVol} rgb="37,99,235" fmt={(v) => `${fmtN(v)} สาย`} unit="สาย" />}
              {inHeat === 'miss' && <Heatmap matrix={m.hmMissVol} rgb="244,63,94" fmt={(v) => `${fmtN(v)} สาย`} unit="สาย" />}
              {inHeat === 'wait' && <Heatmap matrix={m.hmMissWait} rgb="245,158,11" fmt={(v) => fmtDur(v)} unit="เวลารอ" count={m.hmMissVol} isMean />}
              {inHeat === 'off' && <Heatmap matrix={m.hmOffHour} rgb="99,102,241" fmt={(v) => `${fmtN(v)} สาย`} unit="นอกเวลา" />}
            </Panel>
          </div>

          {/* OUTBOUND */}
          <div>
            <SectionHead icon={PhoneOutgoing} tone="text-sky-600" title="สายออก (Outbound)" />
            <MindMap
              root={<RootNode label="สายออกทั้งหมด" value={fmtN(m.outTotal)} sub={`เฉลี่ย ${m.outPerDay.toFixed(1)} สาย/วัน`} cur={m.outTotal} prev={prev?.outTotal} />}
              branches={[
                <BranchNode key="ans" label="ติดต่อได้ (รับสาย)" value={`${Math.round(m.outAnsPct)}%`} badge={`${fmtN(m.outAnsN)} สาย`}
                  tone="text-emerald-600" accent="border-emerald-200" cur={m.outAnsPct} prev={prev?.outAnsPct} betterWhen="up"
                  metrics={[{ label: 'สนทนาเฉลี่ย', value: fmtDur(m.outAnsTalk), cur: m.outAnsTalk, prev: prev?.outAnsTalk }]} />,
                <BranchNode key="canc" label="ไม่รับ (ยกเลิก)" value={`${Math.round(m.outCancPct)}%`} badge={`${fmtN(m.outCancN)} สาย`}
                  tone="text-amber-600" accent="border-amber-200" cur={m.outCancPct} prev={prev?.outCancPct} betterWhen="down"
                  metrics={[{ label: 'รอก่อนวางสาย', value: m.outCancN ? fmtDur(m.outCancRing) : '—', cur: m.outCancRing, prev: prev?.outCancRing }]} />,
              ]} />
            <Panel title="แผนที่ความหนาแน่นสายออก · วัน × ชั่วโมง">
              <Heatmap matrix={m.hmOutVol} rgb="14,165,233" fmt={(v) => `${fmtN(v)} สาย`} unit="สาย" />
            </Panel>
          </div>
        </div>
      )}
    </div>
  )
}

function SectionHead({ icon: Icon, title, tone }) {
  return <div className="flex items-center gap-2 mb-3"><Icon size={18} className={tone} /><h3 className="text-[16px] font-semibold text-slate-900">{title}</h3></div>
}

function MindMap({ root, branches }) {
  return (
    <div className="mb-4 flex items-center gap-0 overflow-x-auto pb-1">
      <div className="flex-none">{root}</div>
      <div className="w-4 border-t-2 border-slate-200 flex-none" />
      <div className="mindtree flex flex-col gap-4 flex-none py-1">
        {branches.map((b, i) => <div key={i} className="mind-branch">{b}</div>)}
      </div>
    </div>
  )
}

function RootNode({ label, value, sub, cur, prev }) {
  return (
    <div className="rounded-xl border-2 border-slate-300 bg-white p-4 w-[170px]">
      <div className="text-[12px] text-slate-500 leading-snug">{label}</div>
      <div className="mt-1 text-[26px] font-bold tabular-nums text-slate-900">{value}</div>
      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
        {sub && <span className="text-[11.5px] text-slate-400">{sub}</span>}
        <Trend cur={cur} prev={prev} />
      </div>
    </div>
  )
}

function BranchNode({ icon: Icon, label, value, badge, tone, accent, cur, prev, betterWhen, metrics = [], emphasis, foot, compact }) {
  return (
    <div className={`rounded-xl bg-white p-3.5 ${compact ? 'w-[200px]' : 'w-[300px]'} ${emphasis ? 'border-2 ring-1 ring-rose-100' : 'border'} ${accent}`}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={13} className="text-slate-400 flex-none" />}
        <span className="text-[12.5px] font-medium text-slate-600">{label}</span>
      </div>
      <div className="flex items-baseline gap-2 mt-0.5">
        <span className={`${emphasis ? 'text-[28px]' : 'text-[22px]'} font-bold tabular-nums ${tone}`}>{value}</span>
        {badge && <span className="text-[11.5px] text-slate-400 tabular-nums">{badge}</span>}
        <Trend cur={cur} prev={prev} betterWhen={betterWhen} />
      </div>
      {metrics.length > 0 && (
        <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5">
          {metrics.map((mt) => (
            <div key={mt.label} className="flex items-center justify-between gap-2 text-[12px]">
              <span className="text-slate-500">{mt.label}</span>
              <span className="inline-flex items-center gap-1.5">
                <span className="tabular-nums text-slate-700 font-medium">{mt.value}</span>
                <Trend cur={mt.cur} prev={mt.prev} betterWhen={mt.betterWhen} />
              </span>
            </div>
          ))}
        </div>
      )}
      {foot && <div className="mt-2 pt-2 border-t border-slate-100 text-[11px] text-slate-400 leading-snug">{foot}</div>}
    </div>
  )
}

function Trend({ cur, prev, betterWhen }) {
  if (prev == null || cur == null) return null
  if (prev === 0 && cur === 0) return null
  if (prev === 0) return <span className="text-[11px] font-medium text-slate-400">ใหม่</span>
  const d = ((cur - prev) / Math.abs(prev)) * 100
  const r = Math.round(d)
  if (r === 0) return <span className="text-[11px] text-slate-400">±0%</span>
  const up = d > 0
  const good = betterWhen === 'up' ? up : betterWhen === 'down' ? !up : null
  const color = good === true ? 'text-emerald-600' : good === false ? 'text-rose-600' : 'text-slate-500'
  return <span className={`text-[11px] font-medium ${color}`}>{up ? '↑' : '↓'} {Math.abs(r)}%</span>
}

// Close-quality: headline % of missed calls closed with no callback trace, plus a
// per-employee breakdown. Deterrent-by-visibility — the manager sees who clears the
// queue without dialling. A callback via personal mobile / LINE won't show here, so
// it reads as a trend + per-person signal, not a per-case verdict.
function CloseQuality({ m, prev }) {
  if (!m.closedN) return <EmptyState variant="compact" title="ไม่มีเคสที่ปิดในช่วงนี้" />
  const emps = m.closeByEmp.filter((e) => e.total > 0)
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
        <span className="text-[24px] font-bold tabular-nums text-rose-600">{m.closedNoCbPct.toFixed(0)}%</span>
        <span className="text-[12.5px] text-slate-500">ปิด {fmtN(m.closedNoCbN)} จาก {fmtN(m.closedN)} เคส โดยไม่มีสายโทรกลับในระบบ</span>
        <Trend cur={m.closedNoCbPct} prev={prev?.closedNoCbPct} betterWhen="down" />
      </div>
      <p className="text-[11px] text-slate-400 mb-3 leading-snug">
        นับเคส “ไม่ได้รับสาย” ที่กดเสร็จสิ้น แต่ไม่พบสายออกโทรกลับ/สายรับกลับถึงเบอร์นั้น · โทรจากมือถือหรือ LINE จะไม่ถูกบันทึก จึงใช้ดูแนวโน้มและรายบุคคล ไม่ใช่ตัดสินรายเคส
      </p>
      <div className="space-y-1.5">
        {emps.map((e) => (
          <div key={e.code} className="flex items-center gap-3 text-[12.5px]">
            <span className="w-40 flex-none text-slate-600 truncate" title={e.code}>{e.code}</span>
            <div className="flex-1 h-4 rounded bg-slate-100 overflow-hidden"><div className="h-full bg-rose-400" style={{ width: `${e.pct}%` }} /></div>
            <span className="w-28 flex-none text-right tabular-nums text-slate-500">{fmtN(e.noCb)}/{fmtN(e.total)} · {e.pct.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Panel({ title, right, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 mb-3">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="text-[13px] font-semibold text-slate-800">{title}</div>{right}
      </div>
      {children}
    </div>
  )
}

const DOW_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']
const DOW_FULL = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']

const TREND_PRESETS = opts([['180d', '6 เดือน'], ['365d', '1 ปี'], ['730d', '2 ปี'], ['custom', 'กำหนดเอง']])

// Self-contained %missed trend panel — grouped BY MONTH via the server-side
// aggregate (type=stat), so it's one fast query even across years. Owns its own
// period filter, independent of the dashboard's date range: keep the KPI cards on
// "7 วัน" while eyeballing the monthly miss rate over the whole year.
// NOTE: the monthly aggregate covers ALL patients (server can't split the cohort),
// so it does not follow the ประเภทคนไข้ toggle.
function MissedTrendPanel() {
  const [preset, setPreset] = useState('180d')
  const [cStart, setCStart] = useState(() => `${todayBkk().slice(0, 4)}-01-01`)
  const [cEnd, setCEnd] = useState(() => todayBkk())
  const [monthMap, setMonthMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [tick, setTick] = useState(0)
  const forceRef = useRef(false) // set by the refresh button → bypass the 1h edge cache

  const range = useMemo(() => {
    let start, end
    if (preset === 'custom') { start = cStart; end = cEnd }
    else { end = todayBkk(); start = shiftDay(end, -(preset === '730d' ? 729 : preset === '365d' ? 364 : 179)) }
    if (start > end) [start, end] = [end, start]
    return { start, end }
  }, [preset, cStart, cEnd])

  useEffect(() => {
    let alive = true; setLoading(true); setError(null)
    const force = forceRef.current; forceRef.current = false
    fetchStat(range.start, range.end, force)
      .then((mp) => { if (alive) { setMonthMap(mp); setLoading(false) } })
      .catch((e) => { if (alive) { setError(e.message || 'โหลดไม่สำเร็จ'); setLoading(false) } })
    return () => { alive = false }
  }, [range, tick])

  const series = useMemo(() => monthsBetween(range.start, range.end).map((key) => {
    const b = monthMap[key] || { ans: 0, miss: 0 }
    const office = b.ans + b.miss
    const [yy, mm] = key.split('-')
    return { short: `${TH_MONTHS_SHORT[+mm - 1]} ${yy.slice(2)}`, label: fmtMonth(`${key}-01`), office, miss: b.miss, missPct: office ? (b.miss / office) * 100 : null }
  }), [monthMap, range])
  const avg = useMemo(() => { let a = 0, m = 0; for (const s of series) { a += s.office; m += s.miss } return a ? (m / a) * 100 : null }, [series])

  return (
    <Panel title="แนวโน้ม % สายไม่ได้รับ · รายเดือน (ในเวลาทำการ)"
      right={
        <div className="flex items-center gap-2 flex-wrap">
          <Segmented size="sm" value={preset} onChange={setPreset} options={TREND_PRESETS} scroll />
          <IconButton size="sm" icon={RefreshCw} label="รีเฟรช (ดึงข้อมูลใหม่ ข้ามแคช)" disabled={loading}
            onClick={() => { forceRef.current = true; setTick((t) => t + 1) }} />
        </div>
      }>
      {preset === 'custom' && (
        <div className="mb-3">
          <DateRangePicker className="w-[260px]" value={{ start: cStart, end: cEnd }}
            onChange={({ start, end }) => { if (start && end) { setCStart(start); setCEnd(end) } }} />
        </div>
      )}
      <div className="mb-2 text-[11.5px] text-slate-400 tabular-nums">{fmtSpan(range.start, range.end)} · รายเดือน · ทุกประเภทคนไข้</div>
      {error ? (
        <ErrorState message={error} onRetry={() => setTick((t) => t + 1)} />
      ) : loading ? (
        <Loading label="กำลังโหลดข้อมูลย้อนหลัง…" />
      ) : (
        <MissedTrend series={series} avg={avg} />
      )}
    </Panel>
  )
}

// SVG %missed line. Line = office-hour miss rate (the KPI); faint bars behind = call
// volume that bucket, so a %spike on a low-volume bucket reads as noise, not a real
// regression. Dashed line = average across the shown span. Hover any point for detail.
function MissedTrend({ series, avg }) {
  const [hi, setHi] = useState(null)
  const valid = series.filter((s) => s.missPct != null)
  if (valid.length < 2) {
    return <EmptyState variant="plain" title="ข้อมูลไม่พอสำหรับกราฟแนวโน้ม" hint="ต้องมีอย่างน้อย 2 ช่วงที่มีสายเข้าในเวลาทำการ" />
  }
  const W = 820, H = 240, padL = 34, padR = 14, padT = 14, padB = 30
  const plotW = W - padL - padR, plotH = H - padT - padB
  const n = series.length
  const maxV = Math.max(...valid.map((s) => s.missPct))
  const yMax = Math.min(100, Math.max(20, Math.ceil((maxV + 2) / 10) * 10))
  const maxVol = Math.max(1, ...series.map((s) => s.office))
  const x = (i) => padL + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW)
  const y = (v) => padT + (1 - v / yMax) * plotH
  const barW = Math.max(2, Math.min(22, (plotW / n) * 0.6))

  // line path, broken across empty buckets
  let d = '', pen = false
  series.forEach((s, i) => { if (s.missPct == null) { pen = false; return } d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)} ${y(s.missPct).toFixed(1)} `; pen = true })
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(t * yMax))
  const labelStep = Math.ceil(n / 12)
  const hovered = hi != null ? series[hi] : null

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 'auto' }} preserveAspectRatio="xMidYMid meet">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="#eef2f6" strokeWidth="1" />
            <text x={padL - 6} y={y(t) + 3} textAnchor="end" className="fill-slate-400" style={{ fontSize: 9 }}>{t}%</text>
          </g>
        ))}
        {series.map((s, i) => s.office > 0 ? (
          <rect key={i} x={x(i) - barW / 2} width={barW} y={padT + plotH - (s.office / maxVol) * plotH * 0.9} height={(s.office / maxVol) * plotH * 0.9}
            rx="1.5" fill={hi === i ? '#cbd5e1' : '#eef2f6'} />
        ) : null)}
        {avg != null && (
          <g>
            <line x1={padL} x2={W - padR} y1={y(avg)} y2={y(avg)} stroke="#fb7185" strokeWidth="1.5" strokeDasharray="4 3" />
            <text x={W - padR} y={y(avg) - 4} textAnchor="end" className="fill-rose-400" style={{ fontSize: 9 }}>เฉลี่ย {avg.toFixed(1)}%</text>
          </g>
        )}
        <path d={d} fill="none" stroke="#f43f5e" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {series.map((s, i) => s.missPct != null ? (
          <circle key={i} cx={x(i)} cy={y(s.missPct)} r={hi === i ? 4 : 2.6} fill="#f43f5e" stroke="#fff" strokeWidth="1.4" />
        ) : null)}
        {series.map((s, i) => (i % labelStep === 0 || i === n - 1) ? (
          <text key={i} x={x(i)} y={H - 10} textAnchor="middle" className="fill-slate-400" style={{ fontSize: 9 }}>{s.short}</text>
        ) : null)}
        {series.map((s, i) => (
          <rect key={i} x={x(i) - plotW / n / 2} width={plotW / n} y={padT} height={plotH} fill="transparent"
            onMouseEnter={() => setHi(i)} onMouseLeave={() => setHi((c) => (c === i ? null : c))} />
        ))}
      </svg>
      {hovered && (
        <div className="absolute -translate-x-1/2 -top-1 pointer-events-none rounded-lg bg-slate-900 text-white px-2.5 py-1.5 shadow-xl text-[11.5px] leading-snug whitespace-nowrap z-10"
          style={{ left: `${(x(hi) / W) * 100}%` }}>
          <div className="font-semibold">{hovered.label}</div>
          {hovered.missPct == null
            ? <div className="text-slate-300">ไม่มีสายในเวลาทำการ</div>
            : <>
                <div className="tabular-nums"><span className="text-rose-300 font-semibold">{hovered.missPct.toFixed(1)}%</span> ไม่ได้รับสาย</div>
                <div className="text-slate-300 tabular-nums">{fmtN(hovered.miss)} จาก {fmtN(hovered.office)} สาย</div>
              </>}
        </div>
      )}
      <div className="mt-1 flex items-center gap-3 text-[10.5px] text-slate-400 pl-8 flex-wrap">
        <span className="inline-flex items-center gap-1"><span className="w-3 h-0.5 rounded bg-rose-500" />% ไม่ได้รับสาย</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-200" />ปริมาณสายเข้า (ในเวลา)</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 border-t border-dashed border-rose-400" />เฉลี่ยทั้งช่วง</span>
      </div>
    </div>
  )
}

function Heatmap({ matrix, rgb, fmt, unit, count, isMean }) {
  const [hover, setHover] = useState(null) // { d, h, x, y }
  const max = Math.max(1, ...matrix.flat())
  const total = matrix.flat().reduce((a, b) => a + b, 0)
  const cell = (v) => (v > 0 ? `rgba(${rgb}, ${(0.14 + (v / max) * 0.86).toFixed(3)})` : '#f1f5f9')
  const hv = hover ? matrix[hover.d][hover.h] : 0
  return (
    <div className="overflow-x-auto relative" onMouseLeave={() => setHover(null)}>
      <div className="inline-block min-w-full">
        <div className="flex gap-[3px] pl-12 mb-[3px]">
          {Array.from({ length: 24 }, (_, h) => <div key={h} className="w-6 text-center text-[9px] text-slate-400 tabular-nums">{h % 2 === 0 ? h : ''}</div>)}
        </div>
        {matrix.map((row, d) => (
          <div key={d} className="flex gap-[3px] items-center mb-[3px]">
            <div className="w-11 text-[10px] text-slate-400 text-right pr-1.5">{DOW_TH[d]}</div>
            {row.map((v, h) => (
              <div
                key={h}
                className={`w-6 h-5 rounded-[3px] transition-shadow ${hover && hover.d === d && hover.h === h ? 'ring-2 ring-slate-900/60' : ''}`}
                style={{ backgroundColor: cell(v) }}
                onMouseEnter={(e) => setHover({ d, h, x: e.clientX, y: e.clientY })}
                onMouseMove={(e) => setHover({ d, h, x: e.clientX, y: e.clientY })}
              />
            ))}
          </div>
        ))}
        <div className="flex items-center gap-1.5 mt-2.5 pl-12 text-[10px] text-slate-400">
          <span>น้อย</span>
          {[0.14, 0.3, 0.5, 0.7, 0.9].map((t) => <span key={t} className="w-6 h-3 rounded-[2px]" style={{ backgroundColor: `rgba(${rgb}, ${t})` }} />)}
          <span>มาก ({unit})</span>
        </div>
      </div>
      {hover && (
        <div
          className="fixed z-50 pointer-events-none rounded-lg bg-slate-900 text-white px-3 py-2 shadow-xl text-[12px] leading-snug"
          style={{ left: Math.min(hover.x + 14, (typeof window !== 'undefined' ? window.innerWidth : 9999) - 190), top: hover.y + 16 }}
        >
          <div className="font-semibold">{DOW_FULL[hover.d]} · {String(hover.h).padStart(2, '0')}:00–{String(hover.h).padStart(2, '0')}:59</div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: `rgb(${rgb})` }} />
            <span className="tabular-nums">{fmt(hv)} <span className="text-slate-400">{unit}</span></span>
          </div>
          {count && <div className="text-slate-300 tabular-nums">จาก {count[hover.d][hover.h]} สาย</div>}
          <div className="text-slate-400 tabular-nums">{Math.round((hv / max) * 100)}% ของช่วงพีคสุด{!isMean && total ? ` · ${pct(hv, total)}% ของทั้งหมด` : ''}</div>
        </div>
      )}
    </div>
  )
}
